import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { useMap } from "@vis.gl/react-google-maps";
import { useRef, useState, useEffect } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import { numberDecimal } from "../../utils/numbers";
import { RADIUS } from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { BORDER_RADIUS } from "../../utils/constants";
import RangeTooltip from "./RangeTooltip";

const sxText = {
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const MarkersCards = ({
  markers = [],
  onRemoveMarker,
  onUpdateMarkerRadius,
}) => {
  const map = useMap();
  const containerRef = useRef(null);
  const [showArrows, setShowArrows] = useState(false);
  const [rangeTooltipIndex, setRangeTooltipIndex] = useState(null);

  const { t } = useTranslationApp();

  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current) {
        const { scrollWidth, clientWidth } = containerRef.current;
        setShowArrows(scrollWidth > clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [markers]);

  const scrollLeft = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: -300,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: 300,
        behavior: "smooth",
      });
    }
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
        }}
      >
        {showArrows && (
          <IconButton
            onClick={scrollLeft}
            sx={{
              backgroundColor: "background.paper",
              flexShrink: 0,
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
        <Box
          ref={containerRef}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            overflowX: "auto",
            gap: 2,
            whiteSpace: "nowrap",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            flex: 1,
          }}
        >
          {markers.map((marker, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                flexShrink: 0,
                width: 250,
              }}
            >
              <Card
                sx={{
                  width: 250,
                  backgroundColor: "fields.background",
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                  borderRadius: `${BORDER_RADIUS * 4}px !important`,
                  flex: "0 0 auto",
                  p: 2,
                  position: "relative",
                  cursor: "pointer",
                }}
                onClick={() => {
                  map.setCenter({ lat: marker.lat, lng: marker.lng });
                  map.setZoom(12);
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 3,
                  }}
                  onClick={(e) => {
                    e?.stopPropagation();
                    onRemoveMarker(index);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Typography
                  variant="body1"
                  fontWeight="600"
                  sx={{ ...sxText, mt: 1 }}
                >
                  {marker.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="fields.placeholder"
                  sx={sxText}
                >
                  {marker.longName}
                </Typography>
                <Typography
                  variant="caption"
                  color="fields.placeholder"
                  sx={{ ...sxText, ml: -0.25 }}
                >
                  {`📍 Lat: ${marker.lat.toFixed(4)}, Lng: ${marker.lng.toFixed(
                    4
                  )}`}
                </Typography>
                {marker.type === "radius" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      gap: 0.5,
                      mt: 0.25,
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <Typography
                      variant="caption"
                      color="fields.placeholder"
                      sx={{ ...sxText, display: "block", ml: -0.25 }}
                    >
                      {`🎯 ${t(RADIUS)}: ${numberDecimal(
                        marker.radius / 1000,
                        2
                      )} KM`}
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{ p: 0.25, flexShrink: 0 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setRangeTooltipIndex(index);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      aria-label="edit radius"
                    >
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                )}
              </Card>
              {rangeTooltipIndex === index && marker.type === "radius" && (
                <Box sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
                  <RangeTooltip
                    latitude={marker.lat}
                    longitude={marker.lng}
                    defaultRange={marker.radius}
                    onChange={(radiusMeters) => {
                      onUpdateMarkerRadius?.({ index, radius: radiusMeters });
                    }}
                    open={true}
                    onClose={() => setRangeTooltipIndex(null)}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>
        {showArrows && (
          <IconButton
            onClick={scrollRight}
            sx={{
              backgroundColor: "background.paper",
              flexShrink: 0,
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default MarkersCards;
